import React, { useEffect, useState } from 'react';
import { useProfileState } from '@/contexts/ProfileContext';

/**
 * Global overlay shown when the analysis is being swapped for a new market or
 * refreshed. Sits above the dashboard content and dims it — the current view
 * stays visible under a translucent scrim so the user never sees a bare
 * loading state during a swap.
 *
 * Renders nothing when `analysisTransitioning === false`.
 */
export default function TransitionOverlay() {
  const { analysisTransitioning, transitionLabel } = useProfileState();
  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState('');

  // Coordinated mount/unmount so the CSS fade always plays cleanly.
  useEffect(() => {
    if (analysisTransitioning) {
      if (transitionLabel) setLabel(transitionLabel);
      setVisible(true);
    } else {
      // Delay unmount so the exit animation has time to play (350ms).
      const t = setTimeout(() => setVisible(false), 350);
      return () => clearTimeout(t);
    }
  }, [analysisTransitioning, transitionLabel]);

  if (!visible) return null;
  return (
    <div
      className={`sd-transition-overlay ${analysisTransitioning ? 'is-open' : 'is-closing'}`}
      data-testid="analysis-transition-overlay"
      aria-live="polite"
      role="status"
    >
      <div className="sd-transition-scrim" aria-hidden="true" />
      <div className="sd-transition-card">
        <div className="sd-transition-spinner" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="sd-transition-label">{label || 'Refreshing your insights…'}</div>
      </div>
    </div>
  );
}
